import { ConnectedPosition, Overlay, OverlayPositionBuilder, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import {
  AfterViewInit,
  Directive,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewContainerRef,
  inject,
} from '@angular/core';
import { Subscription, fromEvent, merge } from 'rxjs';
import { debounceTime, distinctUntilChanged, map } from 'rxjs/operators';

@Directive({
  selector: '[mytcTooltip]',
  standalone: false,
})
export class TooltipDirective implements OnInit, AfterViewInit, OnDestroy {
  private overlay = inject(Overlay);
  private overlayPositionBuilder = inject(OverlayPositionBuilder);
  private el = inject(ElementRef);
  private viewContainer = inject(ViewContainerRef);

  private eventSubscription: Subscription;

  private overlayRef: OverlayRef;

  @Input() isDisabled = false;
  @Input() template: TemplateRef<any>;
  @Input() positions: ConnectedPosition[] = [
    {
      originX: 'center',
      originY: 'bottom',
      overlayX: 'center',
      overlayY: 'top',
    },
  ];

  ngOnInit(): void {
    const positionStrategy = this.overlayPositionBuilder.flexibleConnectedTo(this.el).withPositions(this.positions);

    this.overlayRef = this.overlay.create({ positionStrategy });
  }

  ngAfterViewInit(): void {
    this.eventSubscription = merge(
      fromEvent(this.el.nativeElement, 'mouseenter'),
      fromEvent(this.el.nativeElement, 'mouseleave'),
      fromEvent(this.overlayRef.hostElement, 'mouseenter'),
      fromEvent(this.overlayRef.hostElement, 'mouseleave'),
    )
      .pipe(
        map((event) => (event as any).type as 'mouseenter' | 'mouseleave'),
        debounceTime(100),
        distinctUntilChanged(),
      )
      .subscribe((eventName) => {
        if (eventName === 'mouseenter') {
          this.show();
        } else {
          this.hide();
        }
      });
  }

  private show(): void {
    if (!this.overlayRef.hasAttached()) {
      this.overlayRef?.attach(new TemplatePortal(this.template, this.viewContainer));
    }
  }

  private hide(): void {
    this.overlayRef?.detach();
  }

  ngOnDestroy(): void {
    this.overlayRef?.detach();
    this.eventSubscription?.unsubscribe();
  }
}
